<script lang="ts" setup>
import OpenEye from "../../../../assets/icons/eye/24/eye-open.svg";
import ClosedEye from "../../../../assets/icons/eye/24/eye-close.svg";
import CloseIcon from "../../../../assets/icons/close/24/close-grey-second.svg";
import debounce from "../../../../composables/debounce";
import type { MaskaDetail } from "maska";
import type { IInputConfig } from "../../../../shared/interfaces/ui/form/formInputs";
import type {
  IValidatorConfig,
  IValidationError,
} from "../../../../shared/interfaces/validator";
import type { InputTypeHTMLAttribute } from "@vue/runtime-dom";

const props = defineProps<{
  inputConfig: IInputConfig;
  validatorSettings?: IValidatorConfig;
  isRequired?: boolean;
  errorMessage?: string;
  errorState?: boolean;
  clearInput?: boolean;
  autofocus?: boolean;
}>();

const emits = defineEmits([
  "update:modelValue",
  "focus",
  "checkCompareInput",
  "getData",
]);

defineExpose({
  setInputValue,
  startValidation,
  getData,
});

const {$partsValidation} = useNuxtApp();
const maskValues = {
  phone: "+7 (###) ###-##-##",
};

const inputValue = ref("");
const unmaskedInputValue = ref("");
const mask = ref("");
const fieldType = ref<InputTypeHTMLAttribute>("text");
const isPasswordVisible = ref(false);
const input = ref<HTMLInputElement>();

if (props.inputConfig.type) {
  fieldType.value = props.inputConfig.type;
}

const errorInfo = ref<IValidationError>({
  hasError: false,
  errorType: "",
  errorText: "",
});

const handleInput = props.inputConfig.hasDebounce
  ? debounce(onMaska, 200)
  : onMaska;

watch(
  () => props.inputConfig.id,
  (newValue) => {
    inputValue.value = "";
  }
);

watch(
  () => props.inputConfig.defaultValue,
  (newValue) => {
    inputValue.value = String(newValue);
  }
);

watch(
  () => props.clearInput,
  () => {
    if (props.clearInput) {
      inputValue.value = "";
      clearError();
    }
  }
);

onMounted(() => {
  const { maskType, defaultValue } = props.inputConfig;
  inputValue.value = defaultValue ?? "";
  mask.value = (maskType && maskValues[maskType]) || "";

  if (props.inputConfig.isFocused) {
    input.value?.focus();
  }
});

function clearError() {
  errorInfo.value.hasError = false;
  errorInfo.value.errorType = "";
  errorInfo.value.errorText = "";
}

function getData() {
  return {
    id: props.inputConfig.id,
    value: unmaskedInputValue.value,
    errorInfo: errorInfo.value,
  };
}

function startValidation(callFromOutside?: boolean) {
  if (props.validatorSettings) {
    const validation = $partsValidation.validator(
      props.validatorSettings,
      unmaskedInputValue.value
    );
    errorInfo.value = validation.errorInfo;

    if (props.validatorSettings.compareWith && !callFromOutside) {
      emits("checkCompareInput", props.validatorSettings.compareWith.elementId);
    }
  }
}

function onMaska(event: CustomEvent<MaskaDetail>) {
  unmaskedInputValue.value = event.detail.unmasked;

  if (!props.inputConfig.validationOnEnter) {
    startValidation();
  }

  emits("update:modelValue", unmaskedInputValue.value);
  emits("getData", getData());
}

function setInputValue(newValue: string) {
  inputValue.value = newValue;
}

function handleInputFocus(isFocused: boolean) {
  emits("focus", isFocused);

  if(isFocused && inputValue.value.length === 0 && props.inputConfig.id === "phone") {
    inputValue.value = "+7" + inputValue.value;
  }
}

function handlePasswordEye() {
  if (isPasswordVisible.value) {
    fieldType.value = "password";
    isPasswordVisible.value = false;
  } else {
    fieldType.value = "text";
    isPasswordVisible.value = true;
  }
}

function leaveOnlyNumbers() {
  if (input.value) {
    input.value.value = input.value.value.replace(/[^\d]/g, "");
    if (
      props.validatorSettings?.maxLength &&
      input.value.value.length > props.validatorSettings?.maxLength
    ) {
      input.value.value = input.value.value.substring(
        0,
        props.validatorSettings?.maxLength
      );
    }
  }
}

function leaveOnlyLetters() {
  if (input.value) {
    input.value.value = input.value.value.replace(/[^a-zа-я`'é-]/giu, "");
    if (input.value.value.length > 100) {
      input.value.value = input.value.value.substring(0, 100);
    }
  }
}

function handleEmailInput() {
  if (input.value) {
    if (input.value.value.length > 100) {
      input.value.value = input.value.value.substring(0, 100);
    }
  }
}

function setMaxLength() {
  if (input.value && input.value.value.length > 256) {
    input.value.value = input.value.value.substring(0, 256);
  }
}

function pasteSymbols() {
  setMaxLength();

  if (
    props.inputConfig.id === "firstName" ||
    props.inputConfig.id === "lastName"
  ) {
    leaveOnlyLetters();
  }

  if (props.inputConfig.onlyNumbers) {
    leaveOnlyNumbers();
  }

  if (props.inputConfig.id === "email") {
    handleEmailInput();
  }


}
</script>

<template>
  <div
    class="input"
    :class="[
      inputConfig.interactiveLabel && 'is_interactive',
      inputConfig.type === 'password' && 'input_password ',
    ]"
  >
    <div class="input-wrapper">
      <input
        v-model="inputValue"
        v-maska
        :data-maska="mask"
        :autofocus="autofocus"
        @maska="handleInput"
        @focusin="handleInputFocus(true)"
        @focusout="handleInputFocus(false)"
        :type="fieldType"
        class="input-field"
        :id="inputConfig.id"
        :disabled="inputConfig.disabled"
        :class="[
          (errorInfo.hasError || errorMessage || errorState) && 'error',
          inputConfig.size,
          inputConfig.backgroundColor,
          inputConfig.hasClear && 'clear-padding',
          inputConfig.icon && 'icon-padding',
        ]"
        :placeholder="inputConfig.placeholder"
        ref="input"
        v-on="
          inputConfig.onlyNumbers
            ? { input: leaveOnlyNumbers }
            : inputConfig.id === 'firstName' || inputConfig.id === 'lastName'
            ? { input: leaveOnlyLetters }
            : inputConfig.id === 'email'
            ? { input: handleEmailInput }
            : {}
        "
        @paste="pasteSymbols"
        @input="setMaxLength"
      />
      <component
        v-if="inputConfig.icon"
        class="input__icon"
        :is="inputConfig.icon"
      />
      <label
        v-if="inputConfig.label"
        class="input-label"
        :for="inputConfig.id"
        :class="[
          inputValue && props.inputConfig.interactiveLabel && 'is_focused',
        ]"
      >
        {{ inputConfig.label }}
        <span
          v-if="validatorSettings?.required || isRequired"
          class="input-label__required"
        >
          *
        </span>
      </label>
      <OpenEye
        class="input__eye input__eye_open"
        v-if="
          inputConfig.type === 'password' && isPasswordVisible && inputValue
        "
        @click="handlePasswordEye"
      />
      <ClosedEye
        class="input__eye input__eye_close"
        v-else-if="
          inputConfig.type === 'password' && !isPasswordVisible && inputValue
        "
        @click="handlePasswordEye"
      />
      <Transition name="fade">
        <CloseIcon
          class="input__clear"
          v-if="inputConfig.hasClear && inputValue"
          @click="inputValue = ''"
        />
      </Transition>
    </div>
    <div
      v-show="
        (errorInfo.hasError || errorMessage) && !inputConfig.hideErrorMessage
      "
      class="input-error"
    >
      {{ errorInfo.errorText || errorMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.input {
  position: relative;
  width: 100%;

  &__icon {
    position: absolute;
    top: 50%;
    transform: translate(0px, -50%);
    left: 12px;
  }

  &.error {
    .input-field {
      box-shadow: 0 0 0 2px $control-border_error;
    }
  }

  &__eye {
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
  }

  &__clear {
    position: absolute;
    top: calc(50% - 12px);
    right: 12px;
    cursor: pointer;

    &:hover:not(:active) {
      @include mf-breakpoint {
        :deep(path) {
          fill: $content_primary;
        }
      }
    }
  }

  &-wrapper {
    display: flex;
    flex-direction: column-reverse;
    position: relative;
  }

  &-label {
    &__required {
      margin-left: -3px;
      color: $control-content_negative;
    }
  }

  &-field {
    width: 100%;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px $control-border_grey;
    padding: 16px 18px;
    transition: box-shadow 0.3s;
    @include font(16px, 20px);
    appearance: none;
    -webkit-appearance: none;
    outline: none;

    &:disabled {
      background: $control-bg_disabled !important;
      pointer-events: none;
      box-shadow: none;
      color: $control-content_disabled !important;

      &+.input-label {
        color: $control-content_disabled !important;
      }
    }

    &.xs {
      height: 36px;
      padding: 8px 12px;

      &::placeholder {
        @include font(14px, 20px);
      }
    }

    &.M {
      padding: 12px 16px;
      @include font(16px, 24px);
    }

    &.l {
      height: 48px;
      padding: 12px 16px;
      @include font(16px, 24px);
    }

    &.gray {
      border-radius: 6px;
      background: $bg_light-grey;
      border: none;
      box-shadow: none;

      &.icon-padding {
        padding-left: 42px;
      }
    }

    &.error {
      box-shadow: inset 0 0 0 1px $control-border_error;
    }

    &.clear-padding {
      padding: 8px 28px 8px 12px;
    }

    &.icon-padding {
      padding: 8px 28px 8px 34px;
    }

    &:focus:not(.error) {
      box-shadow: inset 0 0 0 2px $control-content_primary;
    }

    &:hover:not(.error) {
      @include mf-breakpoint {
        box-shadow: inset 0 0 0 2px $control-content_primary;
      }
    }

    &::placeholder {
      color: $content_secondary;
      @include font(16px, 20px);
    }

    &:disabled {
      opacity: 1 !important;

      &:hover {
        box-shadow: 0 0 0 1px $control-border_grey;
      }
    }
  }

  &-error {
    margin-top: 4px;
    width: 100%;
    color: $content_negative;
    @include font(14px, 20px);
  }

  &.is_interactive {
    .input-field {
      padding: 28px 16px 8px;
    }

    .input-label {
      position: absolute;
      top: 50%;
      left: 16px;
      transform: translateY(-50%);
      color: $content_secondary;
      pointer-events: none;
      cursor: text;
      transition: 0.2s all;
      @include font(16px, 20px);

      &.is_focused {
        top: 8px;
        transform: translateY(0);
        @include font(12px, 14px);
      }
    }

    .input-field:focus+label,
    .input-field:-webkit-autofill:focus+label,
    .input-field:-webkit-autofill+label {
      top: 8px;
      transform: translateY(0);
      @include font(12px, 14px);
    }

    .input-field:focus,
    .input-field:-webkit-autofill:focus,
    .input-field:-webkit-autofill {
      top: 8px;
    }
  }
}
</style>
